import { Input as InputPrimitive } from '@base-ui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { mergeStyling } from '../../common-functions';

const Input = ({ className, type, ...props }: React.ComponentProps<'input'>) => {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={mergeStyling(
        'h-8 w-full min-w-0 rounded-sm border border-gray-4 bg-transparent px-2.5 py-1.5 text-base outline-hidden file:inline-flex file:h-8 file:border-0 file:bg-transparent file:font-medium file:text-gray-1 file:text-sm placeholder:text-gray-3 focus-visible:border-primary-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-5 disabled:opacity-50 aria-invalid:border-error md:h-8 md:py-1 md:text-sm md:file:h-8',
        className
      )}
      {...props}
    />
  );
};

const Textarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <textarea
      data-slot="textarea"
      className={mergeStyling(
        'field-sizing-content flex min-h-16 w-full rounded-sm border border-gray-4 bg-transparent px-2.5 py-2 text-base outline-hidden placeholder:text-gray-3 focus-visible:border-primary-2 disabled:cursor-not-allowed disabled:bg-gray-5 disabled:opacity-50 aria-invalid:border-error md:text-sm',
        className
      )}
      {...props}
    />
  );
};

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={mergeStyling(
        'group/input-group relative flex h-8 w-full min-w-0 items-center rounded-sm border border-gray-4 outline-hidden in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-start]]:h-auto has-[>textarea]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:flex-col has-[[data-slot=input-group-control]:focus-visible]:border-primary-2 has-[[data-slot][aria-invalid=true]]:border-error has-disabled:bg-gray-5 has-disabled:opacity-50 md:h-8 has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        className
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text select-none items-center justify-center py-1.5 font-medium text-sm group-data-[disabled=true]/input-group:opacity-50 [&>svg:not([class*='size-'])]:size-5 md:[&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]',
        'inline-end': 'order-last pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end': 'order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={mergeStyling(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={mergeStyling(
        "flex items-center gap-2 text-gray-2 text-sm [&_svg:not([class*='size-'])]:size-5 md:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: React.ComponentProps<'input'>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={mergeStyling(
        'min-w-0 flex-1 border-0 bg-transparent px-2.5 text-base outline-hidden placeholder:text-gray-3 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className
      )}
      {...props}
    />
  );
};

const InputGroupTextarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={mergeStyling(
        'flex-1 resize-none border-0 bg-transparent px-2.5 py-2 text-base outline-hidden placeholder:text-gray-3 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className
      )}
      {...props}
    />
  );
};

const InputGroupButton = ({ className, type = 'button', ...props }: React.ComponentProps<'button'>) => {
  return (
    <button
      type={type}
      data-slot="input-group-button"
      className={mergeStyling(
        "flex cursor-pointer items-center justify-center gap-1 rounded-sm px-1.5 py-1 text-gray-2 text-sm transition-colors hover:bg-gray-5 hover:text-gray-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-2 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
};

export {
  Input,
  Textarea,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
  InputGroupButton,
};
